import { EntityParameters } from "./gameElement";
import { ArrayVec2 } from "./synchronization";
import { Player } from "./player";
import { Composition, CompositionElement } from "./composition";
import { GameElement } from "./gameElementTypes";
import { TrainingLevel } from "./playerAccount";
import { type } from "os";

export enum SyncMessageType {
	Acknowledge,
	GotoSimulation,
	GotoShop,
	GotoLobby,
	State,
	Event,
	ChainSync,
	TargetChange,
	PlayerSync,
	PlayerReward,
	ShopConfirmBuy,
	ShopConfirmSell,
	ShopConfirmRefresh,
	ShopConfirmExp,
	ShopForceMove,
	ShopDragTrigger,
	VirusGrowth,
	Tiebreaker,
	NextLane,
	PlayerConnected,
	DataSaved,
	ReassignPlayerId,
	RoundStats,
	PassThrough,
	StepEnd,
	BotMoveEnd,
	PlayerDisconnected,
	ServerCrashed,
}

type ServerMessageBase = {
	noQueue?: boolean
};

export type ServerMessage = ServerMessageBase
	& (AcknowledgeMessage
		| GotoSimulationSignal
		| GotoShopSignal
		| GotoLobbySignal
		| SimulationSyncMessage
		| PlayerSync
		| PlayerReward
		| ShopConfirmBuy
		| ShopConfirmRefresh
		| ShopConfirmSell
		| ShopForceMove
		| ShopConfirmExp
		| ShopDragTrigger
		| VirusGrowthMessage
		| Tiebreaker
		| NextLane
		| PlayerConnectedSignal
		| GameSavedSignal
		| ReassignPlayerId
		| RoundStatsMessage
		| PassThroughServerMessage
		| StepEndSignal
		| BotMoveEndSignal
		| PlayerDisconnectedSignal
		| ServerCrashedSignal
	)

export type RoundStatsMessage = {
	messageType: SyncMessageType.RoundStats,
	stats: { [key: number]: any },
}

export type GotoSimulationSignal = {
	messageType: SyncMessageType.GotoSimulation;
}

export type GotoShopSignal = {
	messageType: SyncMessageType.GotoShop;
	stepNumber: number;
	survivors: Array<number>;
	newRoundNumber: number;
	virusGrowth: VirusGrowthMessage[];
}

export type GotoLobbySignal = {
	messageType: SyncMessageType.GotoLobby;
	playersAlive: Array<number>;
}

export type AcknowledgeMessage = {
	messageType: SyncMessageType.Acknowledge;
	assignedPlayerID: number;
	isReconnection: boolean;
}

export type PlayerSync = {
	messageType: SyncMessageType.PlayerSync;
	players: Player[];
}


export type ShopConfirmBuy = {
	messageType: SyncMessageType.ShopConfirmBuy,
	playerId: number,
	isReward: boolean;
	offerIndex: number,
	item: CompositionElement, //need all info server decided on like id, rotation, etc
}

export type ShopConfirmSell = {
	messageType: SyncMessageType.ShopConfirmSell,
	playerId: number,
	id: number,
}

export type ShopForceMove = {
	messageType: SyncMessageType.ShopForceMove,
	playerId: number,
	isUndo: boolean,
	isGroupMove: boolean,
	id: number,
	position: ArrayVec2,
	rotation: number,
	physicsPosition: ArrayVec2
}

export type PlayerReward = {
	messageType: SyncMessageType.PlayerReward,
	id: number,
	reward: GameElement[],
}

export type ShopConfirmExp = {
	messageType: SyncMessageType.ShopConfirmExp,
	didLevel: boolean,
}

export type ShopDragTrigger = {
	messageType: SyncMessageType.ShopDragTrigger,
	playerId: number,
	dropperId: number,
	droppedId: number,
	atPosition: ArrayVec2,
}

export type ShopConfirmRefresh = {
	messageType: SyncMessageType.ShopConfirmRefresh,
}

export type SimulationSyncMessage = SimulationStateMessage | SimulationChainSyncMessage | SimulationEventMessage;

export type SimulationStateMessage = {
	messageType: SyncMessageType.State;
	stepNumber: number;
	payload: SyncEntityState[];
}

//this could be expanded to a general start of sim sync message right now as thats teh only time its used
export type SimulationChainSyncMessage = {
	messageType: SyncMessageType.ChainSync;
	connections: string;
}

export type SimulationEventMessage = {
	messageType: SyncMessageType.Event;
	stepNumber: number;
	payload: EventReceipt[];
}

export type EventReceipt = CreationReceipt | DestroyedReceipt | CollisionReceipt | TargetChangeReceipt | MoveTriggerReceipt;

export type MoveTriggerReceipt = {
	id: number,
	stepNumber: number,
	isMoveTrigger: true,
}

export type CollisionReceipt = {
	id: number,
	hitId: number,
	canDamage: boolean
}

export type DestroyedReceipt = {
	id: number,
	type: GameElement,
}

export type CreationReceipt = {
	type: GameElement,
	position: ArrayVec2,
	rotation: number,
	playerId: number,
	id: number,
	rngSeed: number,
	promoted?: boolean,
	entityParameters?: EntityParameters
}

export type TargetChangeReceipt = {
	id: number,
	specIndex: number,
	result: number[],
	receiverType: GameElement, //this is for debugging purposes, sometimes receiver doesn't exist on client and want to know something about it
}

export type SyncEntityState = {
	id: number,
	position: ArrayVec2,
	angle: number,
	direction: ArrayVec2,
	linearVelocity: ArrayVec2,
	angularVelocity: number;
	hp?: number;
	seekTarget?: number;
	orbitTarget?: ArrayVec2;
	straightTarget?: ArrayVec2;
}

export type StepEndSignal = {
	messageType: SyncMessageType.StepEnd,
	stepNumber: number,
}

export type BotMoveEndSignal = {
	messageType: SyncMessageType.BotMoveEnd,
}

export type PlayerDisconnectedSignal = {
	messageType: SyncMessageType.PlayerDisconnected,
	playerId: number,
}

export type ServerCrashedSignal = {
	messageType: SyncMessageType.ServerCrashed,
	error: string,
	noQueue: true, //must not wait behind whatever the client was still processing
}

//Client
export enum ClientAction {
	BuyAntibody = "BUY_ANTIBODY",
	SellAntibody = "SELL_ANTIBODY",
	BuyExperience = "BUY_EXPERIENCE",
	RefreshShop = "REFRESH_SHOP",
	FreezeShop = "FREEZE_SHOP",
	ChooseReward = "CHOOSE_REWARD",
	ChangeState = "CHANGE_STATE",
	MoveAntibody = "MOVE_ANTIBODY",
	MoveInfected = "MOVE_INFECTED",
	MoveCellGroup = "MOVE_GROUP",
	ChangePlayers = "CHANGE_PLAYERS",
	SetName = "SET_NAME",
	EndTurn = "END_TURN",
	DebugForceSimulaton = "DEBUG_FORCE_SIMULATION",
	SaveGame = "LOAD_GAME",
	LoadGame = "SAVE_GAME",
	StartGame = "START_GAME",
	ConfirmReassignedId = "CONFIRM_REASSIGNED_ID",
	DragTrigger = "DRAG_TRIGGER",
	StartEndlessMode = "START_ENDLESS_MODE",
	HostResetGame = "HOST_RESET_GAME",
	PassThrough = "PASS_THROUGH",
	ClientHello = "CLIENT_HELLO",
}

export type ClientMessageType =
	MoveCell
	| MoveCellGroup
	| BuyAntibodyAction
	| SellAntibodyAction
	//| CombineAntibodyAction
	| ChangeStateAction
	| ChangePlayersAction
	| BuyExperienceAction
	| RefreshShopAction
	| ChooseRewardAction
	| FreezeShopAction
	| SetNameAction
	| EndTurnAction
	| DebugForceSimulation
	| SaveGameRequest
	| LoadGameRequest
	| ConfirmReassignedId
	| StartGame
	| DragTriggerAction
	| StartEndlessMode
	| HostResetGame
	| PassThroughClientMessage
	| ClientHelloMessage

export type MoveCell = {
	messageType: ClientAction.MoveAntibody | ClientAction.MoveInfected;
	id: number,
	toPosition: ArrayVec2,
	toPhysicsPosition: ArrayVec2,
	toRotation: number,
}

export type MoveCellGroup = {
	messageType: ClientAction.MoveCellGroup,
	cells: Array<MoveCell>
}

export type BuyAntibodyAction = {
	messageType: ClientAction.BuyAntibody
	index: number,
	isReward: boolean,
	requestedPosition: ArrayVec2
}

export type SellAntibodyAction = {
	messageType: ClientAction.SellAntibody,
	id: number,
}

// export type CombineAntibodyAction = {
// 	messageType: ClientAction.CombineAntibody,
// 	targetId: number,
// 	combinedIdOrShopIndex: number,
// 	isFromShop: boolean
// }

export type BuyExperienceAction = {
	messageType: ClientAction.BuyExperience
}

export type RefreshShopAction = {
	messageType: ClientAction.RefreshShop
}

export type ChooseRewardAction = {
	messageType: ClientAction.ChooseReward
	index: number,
}

export type FreezeShopAction = {
	messageType: ClientAction.FreezeShop
}

export type ChangeStateAction = {
	messageType: ClientAction.ChangeState
	name: string,
	round: number,
}

export type SetNameAction = {
	messageType: ClientAction.SetName
	name: string,
}

export type EndTurnAction = {
	messageType: ClientAction.EndTurn
}

export type DragTriggerAction = {
	messageType: ClientAction.DragTrigger,
	id: number,
	toPosition: ArrayVec2,
}

export type StartEndlessMode = {
	messageType: ClientAction.StartEndlessMode
}

export type ChangePlayersAction = {
	messageType: ClientAction.ChangePlayers,
	player: Player | null,
	opponents: Player[]
}

//Virus progression messages

export enum VirusGrowthType {
	Mutation, Spawn, CountIncrease, Battle
};

export type VirusMutation = {
	messageType: SyncMessageType.VirusGrowth,
	growthType: VirusGrowthType.Mutation,
	type: GameElement,
	damageRequired: number,
	toType: GameElement,
	wave: number,
}

export type VirusSpawn = {
	messageType: SyncMessageType.VirusGrowth,
	growthType: VirusGrowthType.Spawn,
	type: GameElement,
	damageRequired: number,
	increase: number,
	wave: number,
}

export type VirusCountIncrease = {
	messageType: SyncMessageType.VirusGrowth,
	growthType: VirusGrowthType.CountIncrease,
	type: GameElement,
	damageRequired: number,
	increase: number,
	totalIncrease: number,
	wave: number,
}

export type VirusGrowthMessage = VirusMutation | VirusSpawn | VirusCountIncrease;

export type Tiebreaker = {
	messageType: SyncMessageType.Tiebreaker,
	tiebreakerRound: number,
	cellsAdded: CreationReceipt[],
	cellsIncreased: Array<number>,
	owningPlayerId: number,
	stepNumber: number,
	noQueue: true
}

export type NextLane = {
	messageType: SyncMessageType.NextLane,
	playerId: number,
	waveNumbers: number[],
	stepNumber: number,
	noQueue: true
}

export type PlayerConnectedSignal = {
	messageType: SyncMessageType.PlayerConnected;
	names: Player[],
}

export type GameSavedSignal = {
	messageType: SyncMessageType.DataSaved;
	name: string,
	data: string,
	type: "saves" | "replays",
}

export type ReassignPlayerId = {
	messageType: SyncMessageType.ReassignPlayerId,
	from: number,
	to: number,
}

export type StartGame = {
	messageType: ClientAction.StartGame
	trainingLevel: TrainingLevel,
	requiredPlayers: number,
	botReplays: Array<{ name: string, data?: string, replayID?: string }>
}

export type HostResetGame = {
	messageType: ClientAction.HostResetGame
}

//Debug

export type DebugForceSimulation = {
	messageType: ClientAction.DebugForceSimulaton
}

export type SaveGameRequest = {
	messageType: ClientAction.SaveGame,
	gameName: string,
}

export type LoadGameRequest = {
	messageType: ClientAction.LoadGame
	gameName: string,
	selfId: number
	data?: string
}

export type ConfirmReassignedId = {
	messageType: ClientAction.ConfirmReassignedId,
}


export type PassThroughClientMessage = {
	messageType: ClientAction.PassThrough,
	connect: boolean
	payload: any
}

export type ClientHelloMessage = {
	messageType: ClientAction.ClientHello,
	isPassThrough?: boolean,
}

export type PassThroughServerMessage = {
	messageType: SyncMessageType.PassThrough,
	payload: any
}